import type { CommunityDetail } from '@pba/react-network';
import type { CommunityList, CommunityListGlobal } from 'models';
import type { User } from 'features/auth';
import { canManageCommunityProfile } from '../can-manage-community';
import { canCreateContentObject } from '../content-object/permissions';
import { hasMembershipInMultipleCommunitiesProfile } from '../user-roles';
import {
  isCommunitiesAdmin,
  isCommunityAdminProfile,
  isOrganizationAdminProfile,
  isOrganizationsAdmin,
  isSuperAdmin,
} from '../utils';

export const canViewAdminDashboardCommunities = ({ viewer }: { viewer: User }) =>
  isSuperAdmin({ viewer: viewer.railsUserData }) ||
  isOrganizationsAdmin({ viewer: viewer.railsUserData }) ||
  isCommunitiesAdmin({ viewer: viewer.railsUserData });

export const canViewCommunityMembersProfile = ({
  community,
  viewer,
}: {
  community: CommunityListGlobal;
  viewer: User;
}) => Boolean(community.settings.viewMembers) || canManageCommunityProfile({ community, viewer: viewer.railsUserData });

export const canLeaveCommunity = ({ community, viewer }: { community: CommunityListGlobal; viewer: User }) =>
  hasMembershipInMultipleCommunitiesProfile({ viewer: viewer.railsUserData }) &&
  (Boolean(community.settings.allowUnsubscribe) ||
    canManageCommunityProfile({ community, viewer: viewer.railsUserData }));

export const canManageCommunity = ({ community, viewer }: { community: CommunityList; viewer: User }) =>
  canManageCommunityProfile({ community, viewer: viewer.railsUserData });

export const canManageCommunityDetail = ({ community, viewer }: { community: CommunityDetail; viewer: User }) =>
  isSuperAdmin({ viewer: viewer.railsUserData }) ||
  isCommunityAdminProfile({ communityId: community.id, viewer: viewer.railsUserData }) ||
  isOrganizationAdminProfile({ organizationId: community.organization.id, viewer: viewer.railsUserData });

export const canManageCommunityAssets = ({ community, viewer }: { community: CommunityDetail; viewer: User }) =>
  canManageCommunityDetail({ community, viewer });

export const canCreatePostInCommunity = ({ community, viewer }: { community: CommunityDetail; viewer: User }) =>
  canCreateContentObject({ viewer }) &&
  (community.settings.allowPosts || canManageCommunityDetail({ community, viewer }));

export const canLeaveCommunityDetail = ({ community, viewer }: { community: CommunityDetail; viewer: User }) =>
  hasMembershipInMultipleCommunitiesProfile({ viewer: viewer.railsUserData }) &&
  (Boolean(community.settings.allowUnsubscribe) || canManageCommunityDetail({ community, viewer }));

export const canManageSelectedCommunity = ({
  community,
  viewer,
}: {
  community?: { organization: { id: string }; value: string };
  viewer: User;
}) =>
  community !== undefined &&
  (isSuperAdmin({ viewer: viewer.railsUserData }) ||
    isCommunityAdminProfile({ communityId: community.value, viewer: viewer.railsUserData }) ||
    isOrganizationAdminProfile({ organizationId: community.organization.id, viewer: viewer.railsUserData }));
